import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVELS = ["COACHING", "WRITTEN WARNING", "SUSPENSION", "TERMINATION"];
const pill = (l) => (l === "TERMINATION" ? "declined" : l === "COACHING" ? "cleared" : "open");
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" }) : "—");

export default function DisciplineLog({ active }) {
  const [rows, setRows] = useState(null);
  const [member, setMember] = useState("");
  const [date, setDate] = useState("");
  const [level, setLevel] = useState("");
  const [violation, setViolation] = useState("");
  const [expectation, setExpectation] = useState("");
  const [issuedBy, setIssuedBy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchRows = useCallback(() => {
    axios.get(`${API}/discipline`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (active === "disc") fetchRows(); }, [active, fetchRows]);

  const canSubmit = member.trim() && date && level && violation.trim() && issuedBy.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/discipline`, {
        member, date, level, violation, corrective_expectation: expectation, issued_by: issuedBy,
      });
      toast.success(`${res.data.dl_number} recorded — ${level} for ${member}`);
      setMember(""); setDate(""); setLevel(""); setViolation(""); setExpectation(""); setIssuedBy("");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record the action");
    }
    setSubmitting(false);
  };

  return (
    <section className={`chapter utility${active === "disc" ? " active" : ""}`} id="disc">
      <div className="eyebrow">Working record · Appendix A-18 — OIG Element 6 evidence (Ch. 2 discipline ladder)</div>
      <h1>Discipline Log &amp; Written Warning Form</h1>
      <p className="lede">The Element 6 ladder — documented coaching → written warning → suspension from scheduling → termination — only counts if each step leaves a record. This is that record: consistent regardless of role or production volume, signed by the issuer, and the evidence the OIG crosswalk promises. Immediate termination is available for intentional violations (falsified documentation, PHI misuse, billing for services not rendered).</p>

      <div className="formcard" data-testid="disc-form">
        <div className="formcard-title">Record a discipline action (A-18)</div>
        <div className="fld-grid">
          <div className="fld"><label>Workforce member</label><input className="inp" value={member} onChange={(e) => setMember(e.target.value)} placeholder="Name (employee or contracted assist)" data-testid="disc-member" /></div>
          <div className="fld"><label>Date of action</label><input className="inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="disc-date" /></div>
          <div className="fld"><label>Action level</label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="inp sel" data-testid="disc-level"><SelectValue placeholder="Where on the ladder…" /></SelectTrigger>
              <SelectContent>{LEVELS.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="fld"><label>Issued by</label><input className="inp" value={issuedBy} onChange={(e) => setIssuedBy(e.target.value)} placeholder="Lane member issuing the action" data-testid="disc-issued-by" /></div>
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>Violation / conduct (facts, not conclusions)</label>
          <textarea className="inp ta" rows={2} value={violation} onChange={(e) => setViolation(e.target.value)} placeholder="What happened, when, which SOP or Code provision it violates" data-testid="disc-violation" />
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>Corrective expectation (what must change, by when)</label>
          <input className="inp" value={expectation} onChange={(e) => setExpectation(e.target.value)} placeholder="The specific expectation and the consequence of recurrence" data-testid="disc-expectation" />
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="disc-submit">Record discipline action</button>
      </div>

      <h2>Log {rows ? `— ${rows.length} action(s)` : ""}</h2>
      <table data-testid="disc-table">
        <tbody>
          <tr><th>DL #</th><th>Member</th><th>Date</th><th>Level</th><th>Violation / expectation</th><th>Issued by</th></tr>
          {rows === null ? (
            <tr><td colSpan={6}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={6} data-testid="disc-empty">No discipline actions on record.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td className="q">{r.dl_number}</td>
                <td>{r.member}</td>
                <td>{r.date}</td>
                <td><span className={`vr-pill ${pill(r.level)}`} data-testid={`disc-level-${r.dl_number}`}>{r.level}</span></td>
                <td>{r.violation}{r.corrective_expectation ? <div className="cap-sub">Expectation: {r.corrective_expectation}</div> : null}</td>
                <td>{r.issued_by}{r.recorded_by ? <div className="cap-sub">Logged by {r.recorded_by.name} · {fmt(r.created_at)}</div> : null}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Consistency is the defense: same ladder for everyone, every step written down. Entries appear in the Audit Trail and export with the binder. Suspension or termination of a contracted assist also triggers the offboarding checklist (ECH-SOP-8.4) and facility notification where privileges are affected.</p>
    </section>
  );
}
